import { Link } from '@tanstack/react-router';
import type { PlaceCategoryDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Switch } from '@upme/ui/components/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { useMemo, useState } from 'react';
import { EmptyState } from '../../../shared/components/empty-state';

interface Props {
  categories: ReadonlyArray<PlaceCategoryDto>;
}

export function PlaceCategoriesTable({ categories }: Props) {
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return categories.filter((c) => {
      if (!showInactive && !c.isActive) return false;
      if (!term) return true;
      return c.name.toLowerCase().includes(term) || c.slug.toLowerCase().includes(term);
    });
  }, [categories, search, showInactive]);

  if (categories.length === 0) {
    return (
      <EmptyState
        title="Категорий мест пока нет"
        description="Создайте первую категорию для мест."
        cta={
          <Button asChild>
            <Link to="/place-categories/new">Создать категорию</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Поиск по названию или slug"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex items-center gap-2">
          <Switch id="show-inactive" checked={showInactive} onCheckedChange={setShowInactive} />
          <Label htmlFor="show-inactive">Показать неактивные</Label>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">Иконка</TableHead>
            <TableHead>Название</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Порядок</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((c) => (
            <TableRow key={c.id} className="cursor-pointer">
              <TableCell className="text-lg">{c.icon ?? '—'}</TableCell>
              <TableCell>
                <Link
                  to="/place-categories/$categoryId"
                  params={{ categoryId: c.id }}
                  className="hover:underline"
                >
                  {c.name}
                </Link>
              </TableCell>
              <TableCell className="font-mono text-xs">{c.slug}</TableCell>
              <TableCell>{c.sortOrder}</TableCell>
              <TableCell>
                {c.isActive ? (
                  <Badge variant="default">active</Badge>
                ) : (
                  <Badge variant="secondary">inactive</Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
